import { Editor } from 'slate';
import type { NodeEntry } from 'slate';

import { renderText } from './text';
import type { RenderTextOptions } from './text';
import {
  isDrawingElement,
  isInsertDrawingEdit,
  isRemoveDrawingEdit,
  Queries,
} from '../../slate-api';
import type { RenderModelDrawing } from '../../slate-api';
import { getEditColor } from '../../util';
import type { RenderContext } from '../engine/render-context';
import { createTransform } from '../engine/transform';

export function renderDrawing(
  renderContext: RenderContext,
  rMDrawingEntry: NodeEntry<RenderModelDrawing>,
  options: RenderTextOptions
) {
  const { cMPath, editor, offsetX, offsetY } = renderContext;
  const currentUserId = renderContext.withContext('currentUserId');
  const isReadOnly = renderContext.withContext('isReadOnly');
  const [rMDrawing, rMDrawingPath] = rMDrawingEntry;
  const { drawingImgId } = rMDrawing;
  const suggestedEdits = rMDrawing.suggestedEdits ?? [];
  const { baselinePx, inheritDeleteEdit, inheritInsertEdit } = options;

  const insertEdit = suggestedEdits.find(isInsertDrawingEdit) ?? inheritInsertEdit;
  const removeEdit = suggestedEdits.find(isRemoveDrawingEdit) ?? inheritDeleteEdit;

  const { containerHeightPx, containerWidthPx, imgHeightPx, imgWidthPx } =
    Queries.getDrawingDimensions(rMDrawing);

  const { flippedHorizontal, flippedVertical, rotation } =
    Queries.getDrawingVisualProperties(rMDrawing);

  const centerX = containerWidthPx / 2;
  const centerY = baselinePx - containerHeightPx / 2;
  const startX = centerX - imgWidthPx / 2;
  const startY = centerY - imgHeightPx / 2;

  const transform = createTransform({
    origin: {
      x: centerX + offsetX,
      y: centerY + offsetY,
    },
    rotation,
    scale: {
      x: flippedHorizontal ? -1 : 1,
      y: flippedVertical ? -1 : 1,
    },
  });

  renderContext.renderChildren(
    rMDrawingEntry,
    { text: renderText },
    {
      ...options,
      inheritInsertEdit: insertEdit,
      inlineVoid: {
        caret: 'hidden',
        overrideWidthPx: containerWidthPx,
      },
    }
  );

  renderContext.drawImage(startX, startY, imgWidthPx, imgHeightPx, {
    imageId: drawingImgId,
    opacity: removeEdit ? 0.5 : 1,
    transform,
  });

  // Strikethrough
  if (removeEdit)
    renderContext.drawHorizontalLine({
      color: getEditColor(removeEdit, { currentUserId }),
      endX: containerWidthPx,
      startX: 0,
      thickness: 2,
      y: baselinePx - containerHeightPx / 2,
    });

  renderContext.setMouseCursor(0, 0, containerWidthPx, baselinePx, {
    cursor: 'default',
    key: `${rMDrawingPath}-cursor`,
  });

  if (!isReadOnly && cMPath && renderContext.isSelectionInsideCMNode()) {
    const [cMDrawing] = Editor.node(editor, cMPath);
    if (!isDrawingElement(cMDrawing)) throw new Error('Expected drawing');

    renderContext.renderReactComponent(
      0,
      baselinePx - containerHeightPx,
      'ImageResizer',
      { clippable: false, key: `${rMDrawingPath}-image-resizer` },
      {
        centerX: offsetX + centerX,
        centerY: offsetY + centerY,
        drawingEntry: [cMDrawing, cMPath],
      }
    );
  }
}
